import type { PropsWithChildren } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { action, radius, spacing, surface, text } from "@/src/theme/tokens";
import type { ControlPermission } from "./permissions";
import { hasControlPermission } from "./permissions";
import { useControlAccessContext } from "./ControlAccessContext";

export function ControlAccessGate({ children, permission }: PropsWithChildren<{ permission?: ControlPermission }>) {
  const access = useControlAccessContext();
  if (access.status === "loading") {
    return <StateFrame><ActivityIndicator color={action.primary} /><Text>Vérification de l’accès Control Center…</Text></StateFrame>;
  }
  if (access.status === "error") {
    return (
      <StateFrame>
        <Text style={styles.title}>Accès impossible à vérifier</Text>
        <Text style={styles.body}>Une erreur réseau ou serveur empêche de confirmer tes autorisations.</Text>
        <Pressable accessibilityRole="button" onPress={access.retry} style={styles.button}><Text style={styles.buttonText}>Réessayer</Text></Pressable>
      </StateFrame>
    );
  }
  if (access.status !== "allowed") return null;
  if (permission && !hasControlPermission(access.member, permission)) {
    return <StateFrame><Text style={styles.title}>Accès limité</Text><Text style={styles.body}>Ton niveau d’accès ne permet pas d’ouvrir cette section.</Text></StateFrame>;
  }
  return children;
}

function StateFrame({ children }: PropsWithChildren) {
  return <View style={styles.frame}>{children}</View>;
}

const styles = StyleSheet.create({
  frame: { alignItems: "center", backgroundColor: surface.secondary, flex: 1, gap: spacing.md, justifyContent: "center", padding: spacing.xl },
  title: { color: text.primary, fontSize: 22, fontWeight: "800", textAlign: "center" },
  body: { color: text.secondary, maxWidth: 480, textAlign: "center" },
  button: { backgroundColor: action.primary, borderRadius: radius.md, paddingHorizontal: spacing.xl, paddingVertical: spacing.md },
  buttonText: { color: text.inverse, fontWeight: "700" },
});
